"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var IndexedFilter_1 = require("./filter/IndexedFilter");
var ViewProcessor_1 = require("./ViewProcessor");
var ChildrenNode_1 = require("../snap/ChildrenNode");
var CacheNode_1 = require("./CacheNode");
var ViewCache_1 = require("./ViewCache");
var EventGenerator_1 = require("./EventGenerator");
var util_1 = require("@firebase/util");
var Operation_1 = require("../operation/Operation");
var Change_1 = require("./Change");
var PriorityIndex_1 = require("../snap/indexes/PriorityIndex");
/**
 * A view represents a specific location and query that has 1 or more event registrations.
 *
 * It does several things:
 * - Maintains the list of event registrations for this location/query.
 * - Maintains a cache of the data visible for this location/query.
 * - Applies new operations (via applyOperation), updates the cache, and based on the event
 * registrations returns the set of events to be raised.
 * @constructor
 */
var View = /** @class */ (function () {
 /**
 *
 * @param {!Query} query_
 * @param {!ViewCache} initialViewCache
 */
 function View(query_, initialViewCache) {
 this.query_ = query_;
 this.eventRegistrations_ = [];
 var params = this.query_.getQueryParams();
 var indexFilter = new IndexedFilter_1.IndexedFilter(params.getIndex());
 var filter = params.getNodeFilter();
 /**
 * @type {ViewProcessor}
 * @private
 */
 this.processor_ = new ViewProcessor_1.ViewProcessor(filter);
 var initialServerCache = initialViewCache.getServerCache();
 var initialEventCache = initialViewCache.getEventCache();
 // Don't filter server node with other filter than index, wait for tagged listen
 var serverSnap = indexFilter.updateFullNode(ChildrenNode_1.ChildrenNode.EMPTY_NODE, initialServerCache.getNode(), null);
 var eventSnap = filter.updateFullNode(ChildrenNode_1.ChildrenNode.EMPTY_NODE, initialEventCache.getNode(), null);
 var newServerCache = new CacheNode_1.CacheNode(serverSnap, initialServerCache.isFullyInitialized(), indexFilter.filtersNodes());
 var newEventCache = new CacheNode_1.CacheNode(eventSnap, initialEventCache.isFullyInitialized(), filter.filtersNodes());
 /**
 * @type {!ViewCache}
 * @private
 */
 this.viewCache_ = new ViewCache_1.ViewCache(newEventCache, newServerCache);
 /**
 * @type {!EventGenerator}
 * @private
 */
 this.eventGenerator_ = new EventGenerator_1.EventGenerator(this.query_);
 }
 /**
 * @return {!Query}
 */
 View.prototype.getQuery = function () {
 return this.query_;
 };
 /**
 * @return {?Node}
 */
 View.prototype.getServerCache = function () {
 return this.viewCache_.getServerCache().getNode();
 };
 /**
 * @param {!Path} path
 * @return {?Node}
 */
 View.prototype.getCompleteServerCache = function (path) {
 var cache = this.viewCache_.getCompleteServerSnap();
 if (cache) {
 // If this isn't a "loadsAllData" view, then cache isn't actually a complete cache and
 // we need to see if it contains the child we're interested in.
 if (this.query_.getQueryParams().loadsAllData() ||
 (!path.isEmpty() && !cache.getImmediateChild(path.getFront()).isEmpty())) {
 return cache.getChild(path);
 }
 }
 return null;
 };
 /**
 * @return {boolean}
 */
 View.prototype.isEmpty = function () {
 return this.eventRegistrations_.length === 0;
 };
 /**
 * @param {!EventRegistration} eventRegistration
 */
 View.prototype.addEventRegistration = function (eventRegistration) {
 this.eventRegistrations_.push(eventRegistration);
 };
 /**
 * @param {?EventRegistration} eventRegistration If null, remove all callbacks.
 * @param {Error=} cancelError If a cancelError is provided, appropriate cancel events will be returned.
 * @return {!Array.} Cancel events, if cancelError was provided.
 */
 View.prototype.removeEventRegistration = function (eventRegistration, cancelError) {
 var cancelEvents = [];
 if (cancelError) {
 util_1.assert(eventRegistration == null, 'A cancel should cancel all event registrations.');
 var path_1 = this.query_.path;
 this.eventRegistrations_.forEach(function (registration) {
 cancelError /** @type {!Error} */ = cancelError;
 var maybeEvent = registration.createCancelEvent(cancelError, path_1);
 if (maybeEvent) {
 cancelEvents.push(maybeEvent);
 }
 });
 }
 if (eventRegistration) {
 var remaining = [];
 for (var i = 0; i < this.eventRegistrations_.length; ++i) {
 var existing = this.eventRegistrations_[i];
 if (!existing.matches(eventRegistration)) {
 remaining.push(existing);
 }
 else if (eventRegistration.hasAnyCallback()) {
 // We're removing just this one
 remaining = remaining.concat(this.eventRegistrations_.slice(i + 1));
 break;
 }
 }
 this.eventRegistrations_ = remaining;
 }
 else {
 this.eventRegistrations_ = [];
 }
 return cancelEvents;
 };
 /**
 * Applies the given Operation, updates our cache, and returns the appropriate events.
 *
 * @param {!Operation} operation
 * @param {!WriteTreeRef} writesCache
 * @param {?Node} completeServerCache
 * @return {!Array.}
 */
 View.prototype.applyOperation = function (operation, writesCache, completeServerCache) {
 if (operation.type === Operation_1.OperationType.MERGE &&
 operation.source.queryId !== null) {
 util_1.assert(this.viewCache_.getCompleteServerSnap(), 'We should always have a full cache before handling merges');
 util_1.assert(this.viewCache_.getCompleteEventSnap(), 'Missing event cache, even though we have a server cache');
 }
 var oldViewCache = this.viewCache_;
 var result = this.processor_.applyOperation(oldViewCache, operation, writesCache, completeServerCache);
 this.processor_.assertIndexed(result.viewCache);
 util_1.assert(result.viewCache.getServerCache().isFullyInitialized() ||
 !oldViewCache.getServerCache().isFullyInitialized(), 'Once a server snap is complete, it should never go back');
 this.viewCache_ = result.viewCache;
 return this.generateEventsForChanges_(result.changes, result.viewCache.getEventCache().getNode(), null);
 };
 /**
 * @param {!EventRegistration} registration
 * @return {!Array.}
 */
 View.prototype.getInitialEvents = function (registration) {
 var eventSnap = this.viewCache_.getEventCache();
 var initialChanges = [];
 if (!eventSnap.getNode().isLeafNode()) {
 var eventNode = eventSnap.getNode();
 eventNode.forEachChild(PriorityIndex_1.PRIORITY_INDEX, function (key, childNode) {
 initialChanges.push(Change_1.Change.childAddedChange(key, childNode));
 });
 }
 if (eventSnap.isFullyInitialized()) {
 initialChanges.push(Change_1.Change.valueChange(eventSnap.getNode()));
 }
 return this.generateEventsForChanges_(initialChanges, eventSnap.getNode(), registration);
 };
 /**
 * @private
 * @param {!Array.} changes
 * @param {!Node} eventCache
 * @param {EventRegistration=} eventRegistration
 * @return {!Array.}
 */
 View.prototype.generateEventsForChanges_ = function (changes, eventCache, eventRegistration) {
 var registrations = eventRegistration
 ? [eventRegistration]
 : this.eventRegistrations_;
 return this.eventGenerator_.generateEventsForChanges(changes, eventCache, registrations);
 };
 return View;
}());
exports.View = View;

//# sourceMappingURL=View.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/view/View.js
// module id = 90
// module chunks = 0